import React, { useState } from "react";
import { useDispatch } from "react-redux";
import { AlertTriangle, Clock, CreditCard, RefreshCw } from "lucide-react";
import { retryPayment, RETRY_LIMIT } from "../../actions/billing";
import useHelcimPayment from "./useHelcimPayment";

// GET /billing/subscription carries an `access` object that says, in the
// server's own words, what state the account is in and what it costs the
// customer. The banner is driven entirely from it:
//
//   reason        paused   what it means
//   null          false    nothing to say
//   cancelled     false    auto-renew off, still working until endsAt
//   payment_failed false   a renewal failed, grace period running
//   payment_failed true    the grace period ran out, account paused
//   ended         true     the cancelled period is over, account paused
const TONE = {
  cancelled: { tone: "warn", icon: Clock },
  payment_failed: { tone: "danger", icon: CreditCard },
  ended: { tone: "danger", icon: AlertTriangle },
};

/**
 * @param {object} access      the `access` object from GET /billing/subscription
 * @param {Function} onChoosePlan opens the plan picker
 * @param {Function} onRetried  called once a payment has gone through, so the
 *                              page can read the subscription again
 */
export default function AccessBanner({ access, onChoosePlan, onRetried }) {
  const dispatch = useDispatch();
  const { pay, stage: payStage, error: payError } = useHelcimPayment();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");

  const reason = access?.reason;

  // A healthy account has nothing to show, and a reason with no message is a
  // response this build does not know how to word.
  if (!reason || !access?.message) return null;

  const meta = TONE[reason] || { tone: "warn", icon: AlertTriangle };
  const Icon = meta.icon;
  const paused = Boolean(access.paused);

  const retry = async () => {
    setError("");
    setBusy(true);

    const result = await dispatch(retryPayment());

    if (!result?.success) {
      setBusy(false);
      setError(
        result?.code === RETRY_LIMIT
          ? result.message
          : result?.message || "We couldn't start that payment.",
      );
      return;
    }

    // The card is taken in a popup over this page. Nothing to charge means
    // the backend settled it on its own.
    const payment = result.checkout
      ? await pay(result.checkout)
      : { done: true };

    setBusy(false);

    // Closed without paying: the banner stays as it was, to try again from.
    if (!payment.done) {
      if (payment.message) setError(payment.message);
      return;
    }

    onRetried?.();
  };

  return (
    <div className={`bill-access is-${meta.tone}`} role="alert">
      <span className="bill-access__icon">
        <Icon size={18} strokeWidth={2} />
      </span>

      <div className="bill-access__text">
        <strong>
          {reason === "ended"
            ? "Subscription ended"
            : reason === "payment_failed"
              ? paused
                ? "Payment failed — account paused"
                : "Payment failed"
              : "Subscription cancelled"}
        </strong>
        {/* As sent: the server knows the dates and the amounts. */}
        <span>{access.message}</span>
        {(error || payError) && (
          <span className="bill-access__error">{error || payError}</span>
        )}
      </div>

      {reason === "payment_failed" && (
        <button
          type="button"
          className="bill-access__btn"
          onClick={retry}
          disabled={busy}
        >
          {busy ? (
            <span className="install-status-spinner" aria-hidden="true" />
          ) : (
            <RefreshCw size={15} strokeWidth={2.2} />
          )}
          {payStage === "card"
            ? "Waiting for the card…"
            : payStage === "confirming"
              ? "Confirming payment…"
              : busy
                ? "Starting…"
                : "Retry payment"}
        </button>
      )}

      {reason === "ended" && (
        <button type="button" className="bill-access__btn" onClick={onChoosePlan}>
          Choose a plan
        </button>
      )}
    </div>
  );
}
